import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Checkbox, Select, Divider, Button, message } from 'antd';
import PeriodoRangePicker from '../PeriodoRangePicker';
import api from '../../service/api';


const ReprocessarEqualsModal = ({ isVisible, setIsVisible }) => {
  const [gruposOptions, setGruposOptions] = useState([]);
  const [isLoadingGrupos, setIsLoadingGrupos] = useState(false);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState([]);
  const [isExecutando, setIsExecutando] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isConcluido, setIsConcluido] = useState(false);
  const logsEndRef = useRef(null);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  useEffect(() => {
    if (!isVisible) return;

    const fetchGrupos = async () => {
      setIsLoadingGrupos(true);
      try {
        const response = await api.get('/api/Cliente/all-grupos');
        const grupos = Array.isArray(response.data) ? response.data : [];
        setGruposOptions(grupos.map((grupo) => ({ label: grupo, value: grupo })));
      } catch (error) {
        message.error('Erro ao carregar grupos');
      } finally {
        setIsLoadingGrupos(false);
      }
    };

    fetchGrupos();
  }, [isVisible]);

  const gruposValues = useMemo(
    () => gruposOptions.map((option) => option.value),
    [gruposOptions]
  );

  const isAllSelected = gruposValues.length > 0 && selectedGrupos.length === gruposValues.length;
  const isIndeterminate = selectedGrupos.length > 0 && !isAllSelected;
  const hasPeriodoSelecionado =
    Array.isArray(selectedPeriodo)
    && selectedPeriodo.length === 2
    && selectedPeriodo[0]
    && selectedPeriodo[1];
  const canReprocessar = selectedGrupos.length > 0 && hasPeriodoSelecionado && !isExecutando;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedGrupos(gruposValues);
      return;
    }
    setSelectedGrupos([]);
  };

  const addLog = (msg, type = 'info') => {
    const time = new Date().toLocaleTimeString('pt-BR');
    setLogs((prev) => [...prev, { message: msg, type, time }]);
  };

  const eventSourceRef = useRef(null);

  const handleExecutar = async () => {
    setIsExecutando(true);
    setLogs([]);
    setIsConcluido(false);

    const payload = {
      nomesGrupos: selectedGrupos,
      dataInicio: selectedPeriodo[0]?.toISOString(),
      dataFim: selectedPeriodo[1]?.toISOString(),
    };

    try {
      const baseURL = (api.defaults.baseURL || 'http://localhost:5000').replace(/\/$/, '');

      const startRes = await fetch(`${baseURL}/Equals/re-processar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!startRes.ok) throw new Error(`HTTP ${startRes.status}`);

      const { jobId } = await startRes.json();

      await new Promise((resolve) => {
        const es = new EventSource(`${baseURL}/Equals/re-processar/${jobId}/stream`);
        eventSourceRef.current = es;

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            addLog(data.message, data.type);
            if (data.type === 'success' || data.type === 'error') {
              es.close();
              eventSourceRef.current = null;
              resolve();
            }
          } catch {}
        };

        es.addEventListener('close', () => {
          es.close();
          eventSourceRef.current = null;
          resolve();
        });

        es.onerror = () => {
          addLog('Reconectando...', 'info');
        };
      });
    } catch (error) {
      addLog(`Erro: ${error.message}`, 'error');
    } finally {
      setIsExecutando(false);
      setIsConcluido(true);
    }
  };

  const handleFechar = () => {
    if (isExecutando) return;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsVisible(false);
    setSelectedGrupos([]);
    setSelectedPeriodo([]);
    setLogs([]);
    setIsConcluido(false);
  };

  return (
    <Modal
      title="Reprocessar Equals"
      visible={isVisible}
      centered
      onCancel={handleFechar}
      footer={null}
      width={700}
      maskClosable={!isExecutando}
    >
      <Form layout="vertical">
        <Form.Item label="Selecionar grupos">
          <Select
            mode="multiple"
            allowClear
            placeholder="Selecione os grupos"
            value={selectedGrupos}
            onChange={setSelectedGrupos}
            options={gruposOptions}
            loading={isLoadingGrupos}
            maxTagCount="responsive"
            notFoundContent="Nenhum grupo disponivel no momento."
            disabled={isExecutando}
            dropdownRender={(menu) => (
              <>
                <div style={{ padding: '8px 12px' }}>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    disabled={gruposValues.length === 0}
                  >
                    Selecionar todos
                  </Checkbox>
                </div>
                <Divider style={{ margin: '0 0 8px 0' }} />
                {menu}
              </>
            )}
          />
        </Form.Item>

        <Form.Item label="Selecionar periodo do reprocessamento">
          <PeriodoRangePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={['Data inicial', 'Data final']}
            value={selectedPeriodo}
            onChange={setSelectedPeriodo}
            popupMatchSelectWidth={false}
            inputReadOnly
            disabled={isExecutando}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {isConcluido && (
              <Button onClick={handleFechar}>Fechar</Button>
            )}
            <Button
              type="primary"
              disabled={!canReprocessar}
              loading={isExecutando}
              onClick={handleExecutar}
            >
              Executar
            </Button>
          </div>
        </Form.Item>
      </Form>

      {logs.length > 0 && (
        <div
          style={{
            marginTop: 16,
            backgroundColor: '#ffffff',
            borderRadius: 6,
            padding: '10px 14px',
            height: 200,
            overflowY: 'scroll',
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: 12,
            lineHeight: '1.7',
            border: '1px solid #d9d9d9',
            flexShrink: 0,
          }}
        >
          {logs.map((log, i) => (
            <div key={i} style={{ color: '#000000' }}>
              <span style={{ marginRight: 8 }}>[{log.time}]</span>
              {log.message}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      )}
    </Modal>
  );
};

export default ReprocessarEqualsModal;
